import { useEffect, useState } from "react";
import { Eyebrow } from "../../components/ui.jsx";
import { ActionLink, PublicFooter, PublicHeader } from "./PublicChrome.jsx";

const FEED_EVENTS = [
  {
    icon: "↯", tint: "bg-amber-500/15 text-amber-700", actor: "◇ agent · hermes",
    title: "Enriched 12 contacts at Kite Commerce", detail: "Provider waterfall ran server-side · every attempt recorded",
    badge: ["catalog hit", "bg-green-500/12 text-green-700"],
  },
  {
    icon: "◇", tint: "bg-primary/10 text-primary-600", actor: "◇ agent · hermes",
    title: "Saved a hiring signal to company memory", detail: "Evidence and confidence attached · appended, not overwritten",
    badge: ["verified", "bg-green-500/12 text-green-700"],
  },
  {
    icon: "✓", tint: "bg-green-500/12 text-green-700", actor: "◇ agent · atlas",
    title: "Created a follow-up task for Marrow Health", detail: "Due Friday · linked to the lead and its primary contact",
    badge: ["task created", "bg-muted text-muted-foreground"],
  },
  {
    icon: "↗", tint: "bg-green-500/12 text-green-700", actor: "◇ agent · atlas",
    title: "Moved a lead to Evaluation with a note", detail: "Stage history kept · visible in needs-attention views",
    badge: ["stage change", "bg-muted text-muted-foreground"],
  },
  {
    icon: "@", tint: "bg-blue-500/12 text-blue-700", actor: "◇ agent · hermes",
    title: "Drafted expansion outreach to Sarah Kim", detail: "Immutable draft created — nothing sends yet",
    badge: ["held for approval", "bg-primary/10 text-primary-600"], pulse: true,
  },
  {
    icon: "✓", tint: "bg-green-500/12 text-green-700", actor: "● human · priya",
    title: "Approved and sent the exact message", detail: "Delivery policy enforced · outcome written to the timeline",
    badge: ["sent", "bg-green-500/12 text-green-700"],
  },
  {
    icon: "◇", tint: "bg-violet-500/12 text-violet-700", actor: "◇ agent · atlas",
    title: "Logged a call summary to the timeline", detail: "Attributed to the agent · attached to person and company",
    badge: ["memory", "bg-muted text-muted-foreground"],
  },
  {
    icon: "⌕", tint: "bg-pink-500/12 text-pink-700", actor: "⌕ provider · apollo",
    title: "Verified a business email address", detail: "Result reviewed before it touched the canonical record",
    badge: ["verified", "bg-green-500/12 text-green-700"],
  },
];

const FEED_VISIBLE = 4;
const FEED_INTERVAL_MS = 2600;

function LiveAgentFeed() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = window.setInterval(() => setTick((current) => current + 1), FEED_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);
  const visible = Array.from({ length: FEED_VISIBLE }, (_, offset) => {
    const index = (tick - offset + FEED_EVENTS.length * 1000) % FEED_EVENTS.length;
    return { ...FEED_EVENTS[index], key: `${tick - offset}` };
  });
  return (
    <div className="relative rounded-xl bg-card shadow-elevated">
      <div className="flex min-h-12 items-center justify-between border-b border-neutral-100 px-5">
        <span className="flex items-center gap-2 text-xs font-bold"><i className="size-2 animate-blink rounded-full bg-green-500 motion-reduce:animate-none" aria-hidden="true" />Agents at work</span>
        <span className="font-mono text-[10px] uppercase tracking-[.14em] text-muted-foreground">attributed · audited</span>
      </div>
      <div className="grid gap-0 overflow-hidden px-5 py-2" aria-live="off">
        {visible.map((event, position) => (
          <div className={`relative grid grid-cols-[36px_minmax(0,1fr)] gap-3 border-b border-neutral-100 py-4 last:border-b-0 ${position === 0 ? "animate-page-in motion-reduce:animate-none" : ""}`} key={event.key}>
            <span className={`grid size-8 place-items-center rounded-full text-[13px] font-semibold ${event.tint}`} aria-hidden="true">{event.icon}</span>
            <div className="grid min-w-0 gap-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-[10px] text-muted-foreground">{event.actor}</span>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold ${event.badge[1]}`}>
                  {event.pulse && <i className="size-1.5 animate-blink rounded-full bg-primary motion-reduce:animate-none" aria-hidden="true" />}
                  {event.badge[0]}
                </span>
              </div>
              <strong className="text-[13px] leading-5">{event.title}</strong>
              <span className="text-[11px] leading-5 text-muted-foreground">{event.detail}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 rounded-b-xl border-t border-neutral-100 bg-muted/50 px-5 py-3">
        <span className="font-mono text-[10px] uppercase tracking-[.12em] text-muted-foreground">agents work continuously · nothing sent without a person</span>
      </div>
    </div>
  );
}

const AUTONOMOUS_WORK = [
  ["The record maintains itself", "Agents enrich contacts, verify emails, save sourced facts, and log calls and meetings as they happen — continuously, with evidence attached, so the workspace never goes stale between check-ins.", "runs continuously"],
  ["The pipeline moves overnight", "Agents create follow-up tasks, advance lead stages with notes, flag motions that need attention, and keep every list current — so your team opens a workspace that already did the busywork.", "no cron jobs to babysit"],
  ["Outreach drafts itself", "Agents research the account, pick the right contact, and write the message. The draft waits in the approval queue — your team reviews and sends in one click, and the outcome lands back on the timeline.", "held until a person approves"],
];

const MCP_TOOL_SAMPLE = [
  "get_workspace_context", "search_workspace", "get_company_memory", "add_fact",
  "enrich_person", "apply_enrichment_result", "create_lead", "change_lead_stage",
  "list_tasks", "create_task", "draft_email", "get_email_draft_status",
  "set_field_value", "update_list_row", "log_communication", "get_lead_timeline",
];

const USE_CASES = [
  ["Sales", "Qualify multiple motions per account, keep stage history honest, and let agents run research and first-touch drafting while reps run conversations."],
  ["Recruiting", "Track companies, candidates, and roles with the same canonical records — agents source, verify contact details, and schedule the follow-ups."],
  ["Partnerships", "Map the org, remember every conversation, and keep relationship strength and preferred channels current across the whole team."],
  ["Operations", "Build Clay-style tables with typed fields, run bounded enrichment, and audit exactly what every agent and provider did — with credit costs."],
];

const HANDOFF_STEPS = [
  ["01", "One canonical record", "Every company and person exists once. Lists, leads, enrichment, and outreach all link back to it — so agents and people read the same facts with the same evidence."],
  ["02", "Agents work inside boundaries", "Issue scoped keys — from crm:read to outreach:draft — publish shared business context, and let agents work the pipeline over MCP with exactly the access you granted."],
  ["03", "People hold the decision point", "Drafts pause for human approval. Enrichment lands as reviewable evidence. Stage changes, sends, and overrides are all attributed and kept."],
];

const CAPABILITIES = [
  ["◇", "bg-primary/10 text-primary-600", "Append-only memory", "Facts carry evidence, confidence, and verification status. Stale is marked, never deleted — history survives team changes."],
  ["@", "bg-blue-500/12 text-blue-700", "Approval-first outreach", "Agents and teammates draft; administrators approve the exact message. Delivery policies and suppressions enforced on send."],
  ["↯", "bg-amber-500/15 text-amber-700", "Evidence-led enrichment", "A server-controlled provider waterfall with every attempt recorded — inspect results before they touch the record."],
  ["↗", "bg-green-500/12 text-green-700", "Leads and pipelines", "Qualify multiple motions per company with configurable stages, owners, Kanban, and needs-attention views."],
  ["▦", "bg-violet-500/12 text-violet-700", "Typed work tables", "Clay-style lists with custom fields, stages, nested people, and historical values — without touching the core schema."],
  ["⌕", "bg-pink-500/12 text-pink-700", "Complete agent audit", "Every agent request, response, failure, duration, and provider credit — reviewable in one activity ledger."],
];

const CONTROL_PLANE = [
  ["outreach:send", "Policy-controlled delivery. Nothing leaves the workspace without an explicit human approval event."],
  ["agent keys", "Seven scoped permissions with expiry, revocation, and last-used tracking. Grant only what each agent needs."],
  ["memory", "Append-only facts with provenance. Corrections supersede — they never silently rewrite history."],
  ["audit", "Agent requests, responses, failures, duration, and provider-credit usage retained for review."],
  ["roles", "Owner, admin, and member roles with email invitations and domain-matched access requests."],
  ["mcp", "A bearer-scoped Model Context Protocol server, so any compliant agent can work the same records."],
];

function SectionIntro({ eyebrow, title, body, maxTitle = "24ch" }) {
  return (
    <div className="grid gap-4">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[clamp(1.9rem,3.2vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.025em]" style={{ maxWidth: maxTitle }}>{title}</h2>
      {body && <p className="max-w-[62ch] text-sm leading-7 text-muted-foreground">{body}</p>}
    </div>
  );
}

export default function HomePage({ actor }) {
  useEffect(() => { document.title = "RevPal · The agent-driven revenue workspace"; }, []);
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PublicHeader actor={actor} />
      <main>
        <section className="mx-auto grid w-full max-w-[1440px] items-center gap-14 px-5 py-16 sm:px-8 lg:min-h-[calc(100vh-80px)] lg:grid-cols-[minmax(0,1fr)_minmax(460px,.95fr)] lg:px-12 lg:py-24" id="product">
          <div className="grid content-center gap-7">
            <Eyebrow>The agent-driven revenue workspace</Eyebrow>
            <h1 className="max-w-[15ch] text-[clamp(2.6rem,4.8vw,4.25rem)] font-extrabold leading-[1.03] tracking-[-0.035em]">Give agents the pipeline. Keep the decisions.</h1>
            <p className="max-w-[56ch] text-[clamp(1rem,1.4vw,1.15rem)] leading-8 text-muted-foreground">RevPal is the shared workspace where AI agents run your revenue busywork — enriching, researching, drafting, updating — over MCP, on canonical records, with a person at every decision that matters.</p>
            <div className="flex flex-wrap items-center gap-3">
              <ActionLink href={actor ? "/app" : "/signup"} variant="primary" className="min-h-12 px-6 text-sm">{actor ? "Open your workspace" : "Create your workspace"}</ActionLink>
              {!actor && <ActionLink href="/login" className="min-h-12 px-6 text-sm">Sign in</ActionLink>}
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground" aria-label="Built-in controls">
              {["Native MCP server", "Scoped agent keys", "Approval-gated outreach", "Full audit trail"].map((claim) => (
                <li className="flex items-center gap-2" key={claim}><i className="size-1 rounded-full bg-primary" aria-hidden="true" />{claim}</li>
              ))}
            </ul>
          </div>
          <LiveAgentFeed />
        </section>

        <section className="mx-auto grid w-full max-w-[1440px] gap-10 px-5 py-10 sm:px-8 lg:px-12" id="agents" aria-label="Agents working for you">
          <SectionIntro
            eyebrow="Agents working for you"
            title="The busywork runs itself. You keep the judgment calls."
            body="Connect Claude, Codex, or your own agents. They work the same records your team sees — continuously, in the background, inside the boundaries you set."
          />
          <div className="grid gap-6 lg:grid-cols-3">
            {AUTONOMOUS_WORK.map(([title, body, tag]) => (
              <article className="grid content-start gap-4 rounded-xl bg-card p-8 shadow-soft" key={title}>
                <span className="justify-self-start rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[.08em] text-primary-600">{tag}</span>
                <h3 className="max-w-[18ch] text-xl font-semibold tracking-[-0.01em]">{title}</h3>
                <p className="text-sm leading-7 text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1440px] gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[.9fr_1.1fr] lg:items-center lg:px-12 lg:py-24" id="mcp" aria-label="MCP integration">
          <div className="grid content-start gap-6">
            <SectionIntro
              eyebrow="Agent-native, not agent-bolted-on"
              title="Any MCP agent connects in one config block."
              body="RevPal ships a bearer-scoped Model Context Protocol server with 70 tools covering the whole workspace — search, memory, enrichment, leads, tables, tasks, and outreach. Every call runs under the key you issued: reads need crm:read, drafts need outreach:draft, and sending stays behind human approval. Every request, response, duration, and provider credit lands in the audit ledger."
              maxTitle="20ch"
            />
            <ul className="grid gap-2.5 text-sm text-neutral-600">
              {[
                "Works with Claude, Codex, and any MCP-compatible agent or framework",
                "Shared agent-context documents tell every agent how your business runs",
                "Seven scopes, key expiry, revocation, and last-used tracking",
                "Operation keys make mutations idempotent — safe to retry, impossible to double-fire",
              ].map((point) => (
                <li className="flex items-start gap-2.5" key={point}><i className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />{point}</li>
              ))}
            </ul>
          </div>
          <div className="grid gap-4">
            <div className="overflow-hidden rounded-xl bg-card shadow-elevated">
              <div className="flex min-h-11 items-center justify-between border-b border-neutral-100 px-5">
                <span className="font-mono text-[11px] font-semibold text-muted-foreground">mcp.json</span>
                <span className="font-mono text-[10px] uppercase tracking-[.14em] text-muted-foreground">one key · seven scopes</span>
              </div>
              <pre className="overflow-x-auto px-5 py-4 font-mono text-[12px] leading-6 text-neutral-600">{`{
  "mcpServers": {
    "revpal": {
      "url": "https://<your-workspace>/mcp",
      "headers": { "Authorization": "Bearer rvp_····" }
    }
  }
}`}</pre>
              <div className="grid gap-2 border-t border-neutral-100 bg-muted/40 px-5 py-4 font-mono text-[11px] leading-6 text-muted-foreground">
                <span><b className="font-semibold text-primary-600">→ get_workspace_context()</b> · read the org's shared playbook</span>
                <span><b className="font-semibold text-primary-600">→ enrich_person(…)</b> · run the provider waterfall, attach evidence</span>
                <span><b className="font-semibold text-primary-600">→ draft_email(…)</b> · lands in the approval queue, never sends itself</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" aria-label="A sample of available MCP tools">
              {MCP_TOOL_SAMPLE.map((tool) => (
                <span className="rounded-full bg-muted px-2.5 py-1 font-mono text-[10.5px] text-muted-foreground" key={tool}>{tool}</span>
              ))}
              <span className="rounded-full bg-primary/10 px-2.5 py-1 font-mono text-[10.5px] font-semibold text-primary-600">+ 54 more</span>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1440px] px-5 pb-10 sm:px-8 lg:px-12" aria-label="How the handoff works">
          <div className="grid gap-6 lg:grid-cols-3">
            {HANDOFF_STEPS.map(([number, title, body]) => (
              <article className="grid min-h-[250px] content-between gap-10 rounded-xl bg-card p-8 shadow-soft" key={number}>
                <span className="font-mono text-xs font-semibold text-primary">{number}</span>
                <div className="grid gap-3">
                  <h2 className="max-w-[18ch] text-xl font-semibold tracking-[-0.01em]">{title}</h2>
                  <p className="max-w-[52ch] text-sm leading-7 text-muted-foreground">{body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1440px] gap-14 px-5 py-20 sm:px-8 lg:grid-cols-[.72fr_1.28fr] lg:px-12 lg:py-28" id="platform">
          <div className="grid content-start gap-5">
            <Eyebrow>One operating record</Eyebrow>
            <h2 className="max-w-[16ch] text-[clamp(1.9rem,3.2vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.025em]">Everything a revenue motion produces, kept and connected.</h2>
            <p className="max-w-[44ch] text-sm leading-7 text-muted-foreground">The same primitives power every relationship-driven motion — and every one of them is exposed to agents over MCP.</p>
          </div>
          <div className="rounded-xl bg-card px-8 py-2 shadow-soft">
            {CAPABILITIES.map(([icon, tint, title, body]) => (
              <div className="grid gap-4 border-b border-neutral-100 py-6 last:border-b-0 sm:grid-cols-[36px_200px_1fr] sm:items-start" key={title}>
                <span className={`grid size-8 place-items-center rounded-full text-[13px] font-semibold ${tint}`} aria-hidden="true">{icon}</span>
                <h3 className="text-sm font-bold leading-6">{title}</h3>
                <p className="max-w-[58ch] text-sm leading-7 text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto grid w-full max-w-[1440px] gap-10 px-5 pb-20 sm:px-8 lg:px-12" id="use-cases" aria-label="Teams that run on RevPal">
          <SectionIntro
            eyebrow="Every relationship-driven team"
            title="One workspace, four motions."
          />
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {USE_CASES.map(([team, body]) => (
              <article className="grid content-start gap-3 rounded-xl bg-card p-7 shadow-soft" key={team}>
                <h3 className="text-base font-semibold tracking-[-0.01em]">{team}</h3>
                <p className="text-[13px] leading-6 text-muted-foreground">{body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-primary-50/50" id="controls" aria-label="Enterprise controls">
          <div className="mx-auto grid w-full max-w-[1440px] gap-12 px-5 py-20 sm:px-8 lg:px-12 lg:py-24">
            <div className="grid gap-4">
              <span className="font-mono text-[11px] font-semibold uppercase tracking-[.16em] text-primary">The control plane</span>
              <h2 className="max-w-[24ch] text-[clamp(1.9rem,3.2vw,2.6rem)] font-bold leading-[1.08] tracking-[-0.025em]">Agents earn autonomy here the same way people do: with scoped access and a review trail.</h2>
            </div>
            <dl className="grid gap-px overflow-hidden rounded-xl bg-neutral-100 shadow-soft sm:grid-cols-2 lg:grid-cols-3">
              {CONTROL_PLANE.map(([term, definition]) => (
                <div className="grid content-start gap-3 bg-card p-7" key={term}>
                  <dt className="font-mono text-xs font-semibold text-primary-600">{term}</dt>
                  <dd className="max-w-[44ch] text-sm leading-7 text-muted-foreground">{definition}</dd>
                </div>
              ))}
            </dl>
            <p className="font-mono text-[11px] tracking-[.04em] text-muted-foreground">Runs on Cloudflare Workers and D1 · agent access over the Model Context Protocol</p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-[1440px] px-5 py-20 sm:px-8 lg:px-12">
          <div className="grid items-center gap-10 rounded-2xl bg-primary px-8 py-14 text-white shadow-elevated lg:grid-cols-[1fr_auto] lg:px-14">
            <div className="grid gap-4">
              <span className="text-[11px] font-bold uppercase tracking-[.14em] text-primary-200">Start with a clean record</span>
              <h2 className="max-w-[20ch] text-[clamp(2rem,3.6vw,3rem)] font-extrabold leading-[1.06] tracking-[-0.03em]">Create the workspace your agents can earn trust in.</h2>
              <p className="max-w-[52ch] text-sm leading-7 text-primary-100">Create your account, open or join an organization, and connect your first agent over MCP in minutes.</p>
            </div>
            <a className="inline-flex min-h-12 items-center justify-center rounded-lg bg-white px-6 text-sm font-bold text-primary transition-colors hover:bg-primary-50 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white" href={actor ? "/app" : "/signup"}>{actor ? "Open workspace" : "Get started"} <span className="ml-3" aria-hidden="true">→</span></a>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
